import { toast } from "react-toastify";
import { ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AuthContextData, AuthProviderProps } from "./types";
import { decodeToken, getToken, removeToken, storeToken } from "~/utils/token";
import { usePowerGeneratorAreaLoginMutation } from "~/graphql/types";
import { LoginFormData } from "~/pages/Login/components/LoginForm";

const AuthContext = createContext({} as AuthContextData);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [getUserIsLoading, setGetUserIsLoading] = useState(true);
  const [powerPlantName, setPowerPlantName] = useState<string | null>(null);

  const [loginUser, { loading: loginIsLoading }] =
    usePowerGeneratorAreaLoginMutation({ fetchPolicy: "no-cache" });

  const navigate = useNavigate();

  const extractPowerPlantName = (token: string) => {
    const payload = decodeToken<{ powerPlant?: { name?: string } }>(token);
    return payload?.powerPlant?.name ?? null;
  };

  const authLogin = async (formData: LoginFormData) => {
    try {
      const result = await loginUser({
        variables: {
          installation: formData.installation,
          powerPlantId: formData.powerPlantId,
        },
      });

      if (result.data?.powerGeneratorAreaLogin.accessToken) {
        const token = result.data.powerGeneratorAreaLogin.accessToken;
        storeToken(token);
        setIsAuthenticated(true);
        setPowerPlantName(extractPowerPlantName(token));
        navigate("/app/dashboard");
      }
    } catch (err: any) {
      const apolloError = err as ApolloError;
      const errMessage = apolloError.graphQLErrors[0];
      toast.error(
        errMessage?.extensions?.code === "BAD_USER_INPUT"
          ? "Instalação ou usina inválidos"
          : "Houve um problema ao realizar o login",
        { autoClose: 2000 },
      );
    }
  };

  const logout = useCallback(() => {
    removeToken();
    setIsAuthenticated(false);
    setPowerPlantName(null);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    const token = getToken();
    if (token) {
      setIsAuthenticated(true);
      setPowerPlantName(extractPowerPlantName(token));
    }
    setGetUserIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        getUserIsLoading,
        authLogin,
        loginIsLoading,
        logout,
        powerPlantName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
